import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Activity, CheckCircle, XCircle, Loader2, Search, Filter, Clock, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface LogEntry {
  id: string;
  provider_id: string;
  operation_id: string;
  correlation_id: string;
  request_json: any;
  response_json: any;
  status_code: number;
  success: boolean;
  error_summary: string | null;
  duration_ms: number;
  created_at: string;
  operation?: {
    key: string;
    graphql_name: string;
    direction: string;
  };
}

interface Stats {
  total: number;
  successful: number;
  failed: number;
  avgDuration: number;
}

const LogsHealthPage = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [operations, setOperations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<Stats>({ total: 0, successful: 0, failed: 0, avgDuration: 0 });

  const [filterOperation, setFilterOperation] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLog, setSelectedLog] = useState<LogEntry | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);

  useEffect(() => {
    loadData();
  }, [page, filterOperation, filterStatus]);

  const loadData = async () => {
    if (!supabase) return;

    setLoading(true);
    try {
      // Get SuccessWare provider
      const { data: provider } = await supabase
        .from('integration_providers')
        .select('id')
        .eq('slug', 'successware')
        .single();

      if (!provider) return;

      // Load operations for filter
      const { data: ops } = await supabase
        .from('integration_operations')
        .select('id, key, graphql_name')
        .eq('provider_id', provider.id);

      setOperations(ops || []);

      // Build query
      let query = supabase
        .from('integration_logs')
        .select(`
          id,
          provider_id,
          operation_id,
          correlation_id,
          request_json,
          response_json,
          status_code,
          success,
          error_summary,
          duration_ms,
          created_at,
          integration_operations (
            key,
            graphql_name,
            direction
          )
        `, { count: 'exact' })
        .eq('provider_id', provider.id)
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1);

      // Apply filters
      if (filterOperation !== 'all') {
        query = query.eq('operation_id', filterOperation);
      }

      if (filterStatus === 'success') {
        query = query.eq('success', true);
      } else if (filterStatus === 'error') {
        query = query.eq('success', false);
      }

      const { data: logsData, error, count } = await query;

      if (error) throw error;

      // Transform data
      const transformedLogs = (logsData || []).map(log => ({
        ...log,
        operation: Array.isArray(log.integration_operations)
          ? log.integration_operations[0]
          : log.integration_operations
      }));

      setLogs(transformedLogs);

      // Calculate stats
      const { data: statsData } = await supabase
        .from('integration_logs')
        .select('success, duration_ms')
        .eq('provider_id', provider.id);

      if (statsData) {
        const successful = statsData.filter(s => s.success).length;
        const failed = statsData.filter(s => !s.success).length;
        const avgDuration = statsData.reduce((acc, s) => acc + (s.duration_ms || 0), 0) / statsData.length;

        setStats({
          total: statsData.length,
          successful,
          failed,
          avgDuration: Math.round(avgDuration)
        });
      }

    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      log.correlation_id?.toLowerCase().includes(term) ||
      log.operation?.key?.toLowerCase().includes(term) ||
      log.error_summary?.toLowerCase().includes(term)
    );
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
          <Link to="/integrations" className="hover:text-white">Integrations</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/integrations/successware" className="hover:text-white">SuccessWare</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">Logs & Health</span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Logs & Health</h1>
        <p className="text-slate-400">
          Monitor API calls, errors, and performance metrics
        </p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-blue-400" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.total}</div>
              <div className="text-sm text-slate-400">Total Calls</div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-400" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.successful}</div>
              <div className="text-sm text-slate-400">Successful</div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <XCircle className="w-8 h-8 text-red-400" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.failed}</div>
              <div className="text-sm text-slate-400">Failed</div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-purple-400" />
            <div>
              <div className="text-2xl font-bold text-white">{stats.avgDuration}ms</div>
              <div className="text-sm text-slate-400">Avg Duration</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex-1 min-w-[300px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by correlation ID, operation, or error..."
              className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-10 pr-4 py-2 text-white placeholder:text-slate-500"
            />
          </div>
        </div>

        <select
          value={filterOperation}
          onChange={(e) => setFilterOperation(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-lg px-4 py-2 text-white"
        >
          <option value="all">All Operations</option>
          {operations.map(op => (
            <option key={op.id} value={op.id}>{op.key.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-slate-900 border border-slate-800 rounded-lg px-4 py-2 text-white"
        >
          <option value="all">All Status</option>
          <option value="success">Success Only</option>
          <option value="error">Errors Only</option>
        </select>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Status</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Operation</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Time</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Duration</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Correlation ID</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-slate-800/30 transition-colors">
                    <td className="px-4 py-3">
                      {log.success ? (
                        <div className="flex items-center gap-2 text-green-400">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm">Success</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-400">
                          <XCircle className="w-4 h-4" />
                          <span className="text-sm">Error</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <div className="text-sm text-white">{log.operation?.key?.replace(/_/g, ' ') || 'Unknown'}</div>
                        <code className="text-xs text-slate-400">{log.operation?.graphql_name}</code>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-300">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${
                        log.duration_ms < 500 ? 'text-green-400' :
                        log.duration_ms < 1000 ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {log.duration_ms}ms
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs text-slate-400">{log.correlation_id}</code>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="text-sm text-blue-400 hover:text-blue-300"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredLogs.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              No logs found matching your filters
            </div>
          )}
        </div>
      )}

      {/* Log Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg border border-slate-800">
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6 flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">Log Details</h2>
                <p className="text-slate-400 text-sm">Correlation ID: {selectedLog.correlation_id}</p>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-slate-400 hover:text-white"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-400">Operation</label>
                  <div className="text-white mt-1">{selectedLog.operation?.key?.replace(/_/g, ' ')}</div>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Status</label>
                  <div className="mt-1">
                    {selectedLog.success ? (
                      <span className="text-green-400">Success</span>
                    ) : (
                      <span className="text-red-400">Error</span>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Duration</label>
                  <div className="text-white mt-1">{selectedLog.duration_ms}ms</div>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Timestamp</label>
                  <div className="text-white mt-1">{formatDate(selectedLog.created_at)}</div>
                </div>
              </div>

              {/* Error Summary */}
              {selectedLog.error_summary && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div>
                      <div className="font-semibold text-red-300 mb-1">Error Summary</div>
                      <div className="text-red-200 text-sm">{selectedLog.error_summary}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Request */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Request</label>
                <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto">
                  {JSON.stringify(selectedLog.request_json, null, 2)}
                </pre>
              </div>

              {/* Response */}
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Response</label>
                <pre className="bg-slate-950 border border-slate-800 rounded-lg p-4 text-sm text-slate-300 overflow-x-auto">
                  {JSON.stringify(selectedLog.response_json, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogsHealthPage;
